import { Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Checkbox, Chip, Tooltip, CircularProgress } from '@mui/material';
import PaiLink from 'features/ui/base-components/pai-link';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ResearchResult } from 'services/lead-list-research.service';
import { makeStyles } from 'tss-react/mui';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const CollapsibleText: React.FC<{ text: string; maxLength: number }> = ({ text, maxLength = 100 }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setIsExpanded(!isExpanded);
  };

  const displayedText = isExpanded ? text : text.substring(0, maxLength) + (text.length > maxLength ? '...' : '');

  return (
    <div>
      {displayedText}
      {text.length > maxLength && (
        <PaiLink
          text={isExpanded ? t('less') : t('more')}
          inline={!isExpanded}
          ml={!isExpanded ? '4px' : undefined}
          size={'md'}
          variant={'primary'}
          underline={'hover'}
          action={toggleExpand}
        />
      )}
    </div>
  );
};

interface Props {
  leads: ResearchResult[];
  selectionMode?: boolean;
  selectedIndices?: number[];
  onSelect?: (index: number) => void;
  enrichmentStatuses?: {
    [index: number]: {
      enrichmentStatus: string;
      enrichmentError: string;
      email: string;
      phone: string;
    };
  };
  showStatusColumn?: boolean;
  allowSelectAll?: boolean;
}

const LeadsTable: React.FC<Props> = ({ leads, selectionMode = false, selectedIndices = [], onSelect,
  enrichmentStatuses, showStatusColumn = false, allowSelectAll = false }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  // Helper to check if a contact has a LinkedIn URL
  const hasLinkedinUrl = (item: any) =>
    (item.contactLinkedinUrl && item.contactLinkedinUrl.startsWith('http')) ||
    (item.contact?.linkedinUrl && item.contact.linkedinUrl.startsWith('http'));

  return (
    <TableContainer className={classes.tableContainer}>
      <Table>
        <TableHead>
          <TableRow>
            {showStatusColumn && <TableCell style={{ minWidth: '60px' }}>{t('enrichmentStatus')}</TableCell>}
            {selectionMode && <TableCell style={{ minWidth: '40px' }}></TableCell>}
            <TableCell style={{ minWidth: '50px' }}>#</TableCell>
            <TableCell style={{ minWidth: '250px' }}>{t('contactName')}</TableCell>
            <TableCell style={{ minWidth: '300px' }}>{t('role')}</TableCell>
            <TableCell style={{ minWidth: '200px' }}>{t('email')}</TableCell>
            <TableCell style={{ minWidth: '200px' }}>{t('phone')}</TableCell>
            <TableCell style={{ minWidth: '200px' }}>{t('contactLinkedin')}</TableCell>
            <TableCell style={{ minWidth: '300px' }}>{t('leadReason')}</TableCell>
            <TableCell style={{ minWidth: '300px' }}>{t('peopleSearch')}</TableCell>
            <TableCell style={{ minWidth: '200px' }}>{t('company')}</TableCell>
            <TableCell style={{ minWidth: '200px' }}>{t('website')}</TableCell>
            <TableCell style={{ minWidth: '200px' }}>{t('companyLinkedin')}</TableCell>
            <TableCell style={{ minWidth: '200px' }}>{t('industry')}</TableCell>
            <TableCell style={{ minWidth: '300px' }}>{t('size')}</TableCell>
            <TableCell style={{ minWidth: '200px' }}>{t('location')}</TableCell>
            <TableCell style={{ minWidth: '300px' }}>{t('address')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {leads.map((item: any, index: number) => {
            const selectable = allowSelectAll ? true : hasLinkedinUrl(item);
            const checked = selectedIndices.includes(index);
            const enrichment = enrichmentStatuses ? enrichmentStatuses[index] : undefined;
            return (
              <TableRow key={index}>
                {showStatusColumn && (
                  <TableCell>
                    {enrichment ? (
                      enrichment.enrichmentStatus === 'pending' || enrichment.enrichmentStatus === 'in_progress' ? (
                        <CircularProgress size={22} />
                      ) : enrichment.enrichmentStatus === 'completed' ? (
                        <CheckCircleIcon color="success" />
                      ) : enrichment.enrichmentStatus === 'sparse' ? (
                        <Tooltip title={enrichment.enrichmentError || t('sparse')}>
                          <InfoOutlinedIcon color="warning" />
                        </Tooltip>
                      ) : enrichment.enrichmentStatus === 'failed' ? (
                        <Tooltip title={enrichment.enrichmentError || t('failed')}><ErrorIcon color="error" /></Tooltip>
                      ) : (
                        <Chip label={t(enrichment.enrichmentStatus)} size="small" />
                      )
                    ) : null}
                  </TableCell>
                )}
                {selectionMode && (
                  <TableCell>
                    <Checkbox
                      checked={checked}
                      disabled={!selectable}
                      onChange={() => selectable && onSelect && onSelect(index)}
                      inputProps={{ 'aria-label': 'select contact' }}
                    />
                  </TableCell>
                )}
                <TableCell>{index + 1}</TableCell>
                <TableCell>{item.contact?.fullName || item.contact?.name || '-'}</TableCell>
                <TableCell>{item.contact?.role || '-'}</TableCell>
                <TableCell>
                  {item.contact?.email || item.email || '-'}
                  {(item.contact?.email || item.email) &&
                    (item.contact?.enrichmentStatus === 'completed' || item.enrichmentStatus === 'completed') && (
                      <Tooltip title={t('enrichedViaBetterContact')} placement="top">
                        <InfoOutlinedIcon fontSize="small" style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                      </Tooltip>
                    )}
                </TableCell>
                <TableCell>
                  {item.contact?.phone || item.phone || '-'}
                  {(item.contact?.phone || item.phone) &&
                    (item.contact?.enrichmentStatus === 'completed' || item.enrichmentStatus === 'completed') && (
                      <Tooltip title={t('enrichedViaBetterContact')} placement="top">
                        <InfoOutlinedIcon fontSize="small" style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                      </Tooltip>
                    )}
                </TableCell>
                <TableCell>
                  {item.contactLinkedinUrl && item.contactLinkedinUrl.startsWith('http') ? (
                    <a href={item.contactLinkedinUrl} target="_blank" rel="noopener noreferrer">
                      {t('viewProfile')}
                    </a>
                  ) : item.contact?.linkedinUrl && item.contact.linkedinUrl.startsWith('http') ? (
                    <a href={item.contact.linkedinUrl} target="_blank" rel="noopener noreferrer">
                      {t('viewProfile')}
                    </a>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <CollapsibleText text={item.leadReason || '-'} maxLength={50} />
                </TableCell>
                <TableCell>{item.peopleSearch || '-'}</TableCell>
                <TableCell>{item.company?.name || '-'}</TableCell>
                <TableCell>
                  {item.company?.website ? (
                    <a
                      href={item.company.website.startsWith('http') ? item.company.website : `https://${item.company.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.company.website}
                    </a>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  {item.companyLinkedinUrl && item.companyLinkedinUrl.startsWith('http') ? (
                    <a href={item.companyLinkedinUrl} target="_blank" rel="noopener noreferrer">
                      {t('viewPage')}
                    </a>
                  ) : item.company?.linkedinUrl && item.company.linkedinUrl.startsWith('http') ? (
                    <a href={item.company.linkedinUrl} target="_blank" rel="noopener noreferrer">
                      {t('viewPage')}
                    </a>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>{item.company?.industry || '-'}</TableCell>
                <TableCell>{item.company?.size || '-'}</TableCell>
                <TableCell>{item.company?.address?.city || '-'}</TableCell>
                <TableCell>
                  {item.company?.address ? (
                    <>
                      {item.company.address.street || ''}
                      {item.company.address.street ? ', ' : ''}
                      {item.company.address.postalCode || ''} {item.company.address.city || ''}
                      {(item.company.address.postalCode || item.company.address.city) && item.company.address.country ? ', ' : ''}
                      {item.company.address.country || ''}
                    </>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const useStyles = makeStyles()((theme) => ({
  tableContainer: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '8px',
    display: 'block',
    boxSizing: 'border-box',
    overflowX: 'auto',
    '& th': {
      border: 'none',
      padding: '4px 16px',
      fontSize: theme.typography.pxToRem(13),
      backgroundColor: theme.palette.background.paper,
      whiteSpace: 'nowrap',
      fontWeight: 'bold',
    },
    '& td, th': {
      border: 'none',
      borderBottom: '1px solid #ddd',
      padding: '8px 16px',
      textAlign: 'left',
      verticalAlign: 'middle',
      fontSize: theme.typography.pxToRem(13),
    },
    '& tr:last-child td': {
      borderBottom: 'none',
    },
  },
}));

export default LeadsTable;
